import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import sourceService from "../services/sourceService";

// Get all sources with smart caching
export const getSources = createAsyncThunk(
  "sources/getSources",
  async (forceRefresh = false, { getState, rejectWithValue }) => {
    try {
      const state = getState().sources;
      const now = Date.now();

      // Temporary logging to verify caching
      console.log("🔍 getSources called with forceRefresh:", forceRefresh);
      console.log("📊 Cache status:", {
        cached: state.cached,
        lastFetched: state.lastFetched,
        age: state.lastFetched ? (now - state.lastFetched) / 1000 : "never",
        timeout: state.cacheTimeout / 1000,
      });

      // Check if we should use cached data
      if (!forceRefresh && state.cached && state.lastFetched && now - state.lastFetched < state.cacheTimeout) {
        console.log("✅ Using cached sources data");
        // Return cached data with special flag
        return { data: state.sources, fromCache: true };
      }

      console.log("🌐 Fetching fresh sources data");
      // Fetch fresh data
      const sources = await sourceService.getAll();
      return sources;
    } catch (error) {
      console.error("sourcesSlice: Error fetching sources:", error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Get a single source
export const getSource = createAsyncThunk("sources/getSource", async (id, { rejectWithValue }) => {
  try {
    return await sourceService.getById(id);
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Create a new source
export const createSource = createAsyncThunk("sources/createSource", async (sourceData, { rejectWithValue }) => {
  try {
    if (process.env.NODE_ENV === "development") {
      console.log("sourcesSlice: Creating source with data:", sourceData);
    }
    const result = await sourceService.create(sourceData);
    if (process.env.NODE_ENV === "development") {
      console.log("sourcesSlice: Source created successfully:", result);
    }
    return result;
  } catch (error) {
    console.error("sourcesSlice: Error creating source:", error);

    // Extract the error message
    let errorMessage = error.message;

    // If there's a response with data
    if (error.response?.data) {
      if (process.env.NODE_ENV === "development") {
        console.error("sourcesSlice: Error response data:", error.response.data);
      }

      // Handle different error data formats
      if (typeof error.response.data === "object") {
        // If it's an object, try to extract the message or error field
        errorMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data);
      } else if (typeof error.response.data === "string") {
        // If it's a string, use it directly
        errorMessage = error.response.data;
      }
    }

    return rejectWithValue(errorMessage);
  }
});

// Update a source
export const updateSource = createAsyncThunk(
  "sources/updateSource",
  async ({ id, sourceData }, { rejectWithValue }) => {
    try {
      return await sourceService.update(id, sourceData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Delete a source
export const deleteSource = createAsyncThunk("sources/deleteSource", async (id, { rejectWithValue }) => {
  try {
    await sourceService.remove(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Get all users with access to a source
export const getSourceUsers = createAsyncThunk("sources/getSourceUsers", async (sourceId, { rejectWithValue }) => {
  try {
    return await sourceService.getSourceUsers(sourceId);
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Grant/update a user's access to a source
export const setSourceUserAccess = createAsyncThunk(
  "sources/setSourceUserAccess",
  async ({ sourceId, userId }, { rejectWithValue }) => {
    try {
      return await sourceService.setSourceUserAccess(sourceId, userId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Remove a user's access to a source
export const removeSourceUserAccess = createAsyncThunk(
  "sources/removeSourceUserAccess",
  async ({ sourceId, userId }, { rejectWithValue }) => {
    try {
      await sourceService.removeSourceUserAccess(sourceId, userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

const initialState = {
  sources: [],
  currentSource: null,
  loading: false,
  error: null,
  success: false,
  cached: false,
  lastFetched: null,
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
  sourceUsers: [],
  sourceUsersLoading: false,
  sourceUsersError: null,
};

const sourcesSlice = createSlice({
  name: "sources",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentSource: (state, action) => {
      state.currentSource = action.payload;
    },
    clearCurrentSource: (state) => {
      state.currentSource = null;
    },
    invalidateCache: (state) => {
      state.cached = false;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all sources
      .addCase(getSources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSources.fulfilled, (state, action) => {
        // Silent operation
        state.loading = false;
        // Handle both cached and fresh data responses
        if (action.payload && action.payload.data) {
          state.sources = action.payload.data;
        } else {
          state.sources = action.payload;
        }
        state.cached = true;
        state.lastFetched = Date.now();
        state.error = null;
        // Don't set success for GET operations - only for CRUD operations
        // state.success should remain unchanged
      })
      .addCase(getSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get single source
      .addCase(getSource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSource.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSource = action.payload;
      })
      .addCase(getSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create source
      .addCase(createSource.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSource.fulfilled, (state, action) => {
        state.loading = false;
        state.sources.push(action.payload);
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(createSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update source
      .addCase(updateSource.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateSource.fulfilled, (state, action) => {
        state.loading = false;
        state.sources = state.sources.map((source) => (source.id === action.payload.id ? action.payload : source));
        state.currentSource = action.payload;
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(updateSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete source
      .addCase(deleteSource.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteSource.fulfilled, (state, action) => {
        state.loading = false;
        state.sources = state.sources.filter((source) => source.id !== action.payload);
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(deleteSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get source users
      .addCase(getSourceUsers.pending, (state) => {
        state.sourceUsersLoading = true;
        state.sourceUsersError = null;
      })
      .addCase(getSourceUsers.fulfilled, (state, action) => {
        state.sourceUsersLoading = false;
        state.sourceUsers = action.payload;
      })
      .addCase(getSourceUsers.rejected, (state, action) => {
        state.sourceUsersLoading = false;
        state.sourceUsersError = action.payload;
      })

      // Set source user access
      .addCase(setSourceUserAccess.pending, (state) => {
        state.sourceUsersLoading = true;
        state.sourceUsersError = null;
      })
      .addCase(setSourceUserAccess.fulfilled, (state, action) => {
        state.sourceUsersLoading = false;
        if (action.payload) {
          const index = state.sourceUsers.findIndex((u) => u.user_id === action.payload.user_id);
          if (index !== -1) {
            state.sourceUsers[index] = action.payload;
          } else {
            state.sourceUsers.push(action.payload);
          }
        }
      })
      .addCase(setSourceUserAccess.rejected, (state, action) => {
        state.sourceUsersLoading = false;
        state.sourceUsersError = action.payload;
      })

      // Remove source user access
      .addCase(removeSourceUserAccess.pending, (state) => {
        state.sourceUsersLoading = true;
        state.sourceUsersError = null;
      })
      .addCase(removeSourceUserAccess.fulfilled, (state, action) => {
        state.sourceUsersLoading = false;
        state.sourceUsers = state.sourceUsers.filter((u) => u.user_id !== action.payload);
      })
      .addCase(removeSourceUserAccess.rejected, (state, action) => {
        state.sourceUsersLoading = false;
        state.sourceUsersError = action.payload;
      });
  },
});

export const { clearError, clearSuccess, setCurrentSource, clearCurrentSource, invalidateCache } = sourcesSlice.actions;

export default sourcesSlice.reducer;
